"use client";

import * as React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useCart } from "@/lib/cart-context";
import { formatPrice } from "@/lib/format";
import { CheckoutDialog } from "@/components/hyper-or/CheckoutDialog";

export function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, setQty, clear, totalCount, totalPrice } =
    useCart();
  const [checkoutOpen, setCheckoutOpen] = React.useState(false);

  function handleCheckoutClick() {
    setCheckoutOpen(true);
  }

  function handleCheckoutClose(open: boolean) {
    setCheckoutOpen(open);
    // If checkout closed (success or cancel) we also collapse the cart drawer
    // so the user returns to shopping — except when an order is still being
    // finalized (open=true means dialog still showing success state).
    if (!open) {
      closeCart();
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeCart()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-cream text-ink flex flex-col p-0 border-l border-ink/10"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-ink/8 bg-ink text-cream">
          <SheetTitle className="font-display text-cream text-2xl tracking-wide">
            Votre <span className="gold-text italic">Panier</span>
          </SheetTitle>
          <SheetDescription className="text-cream/55 text-[11px] tracking-[0.2em] uppercase">
            {totalCount > 0
              ? `${totalCount} article${totalCount > 1 ? "s" : ""}`
              : "Votre panier est vide"}
          </SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-ink/15 text-ink/40">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path>
                <path d="M3 6h18"></path>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
            </span>
            <p className="font-serif italic text-ink/55 text-lg">
              Aucun article pour l&apos;instant.
            </p>
            <p className="text-ink/45 text-sm leading-relaxed max-w-xs">
              Parcourez nos 12 boutiques de luxe et ajoutez vos pépites — elles
              apparaîtront ici, prêtes à être livrées.
            </p>
            <button
              onClick={closeCart}
              className="mt-2 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-cream hover:bg-gold hover:text-ink transition-colors text-[11px] tracking-[0.2em] uppercase"
            >
              Découvrir les boutiques
            </button>
          </div>
        ) : (
          <>
            {/* Items list */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.map((it) => (
                <div
                  key={it.id}
                  className="flex gap-3 pb-4 border-b border-ink/8 last:border-0"
                >
                  <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-parchment">
                    {it.image && (
                      <img
                        src={it.image}
                        alt={it.name}
                        className="absolute inset-0 h-full w-full object-cover"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display =
                            "none";
                        }}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col">
                    {it.boutiqueName && (
                      <span className="text-[9px] tracking-[0.18em] uppercase text-ink/45">
                        {it.boutiqueName}
                      </span>
                    )}
                    <h4 className="font-serif text-ink text-sm leading-snug line-clamp-2">
                      {it.name}
                    </h4>
                    <div className="mt-auto flex items-center justify-between gap-2">
                      <div className="inline-flex items-center rounded-full border border-ink/15">
                        <button
                          aria-label="Diminuer"
                          onClick={() => setQty(it.id, it.qty - 1)}
                          className="h-7 w-7 inline-flex items-center justify-center text-ink/70 hover:text-gold transition-colors"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M5 12h14"></path>
                          </svg>
                        </button>
                        <span className="min-w-6 text-center text-sm font-medium">
                          {it.qty}
                        </span>
                        <button
                          aria-label="Augmenter"
                          onClick={() => setQty(it.id, it.qty + 1)}
                          className="h-7 w-7 inline-flex items-center justify-center text-ink/70 hover:text-gold transition-colors"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M5 12h14"></path>
                            <path d="M12 5v14"></path>
                          </svg>
                        </button>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-ink text-sm">
                          {formatPrice(it.price * it.qty)}
                        </div>
                        {it.qty > 1 && (
                          <div className="text-[10px] text-ink/45">
                            {formatPrice(it.price)} / unité
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(it.id)}
                    aria-label="Retirer"
                    className="self-start text-ink/35 hover:text-bordeaux transition-colors"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18"></path>
                      <path d="m6 6 12 12"></path>
                    </svg>
                  </button>
                </div>
              ))}

              <button
                onClick={clear}
                className="text-[10px] tracking-[0.18em] uppercase text-ink/45 hover:text-bordeaux transition-colors"
              >
                Vider le panier
              </button>
            </div>

            {/* Footer */}
            <div className="border-t border-ink/8 px-6 py-5 space-y-3 bg-parchment/40">
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink/55 tracking-[0.15em] uppercase text-[11px]">
                  Sous-total
                </span>
                <span className="font-display text-ink text-xl">
                  {formatPrice(totalPrice)}
                </span>
              </div>
              <p className="text-[11px] text-ink/45 leading-relaxed">
                Livraison offerte dès 25 000 FCFA · Click &amp; Collect en 30 min
              </p>
              <button
                onClick={handleCheckoutClick}
                className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-ink text-cream hover:bg-gold hover:text-ink transition-colors text-[11px] tracking-[0.25em] uppercase font-semibold"
              >
                Passer commande
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </button>
              <button
                onClick={closeCart}
                className="w-full text-center text-[10px] tracking-[0.2em] uppercase text-ink/55 hover:text-ink transition-colors py-1"
              >
                Continuer mes achats
              </button>
            </div>
          </>
        )}
      </SheetContent>
      <CheckoutDialog open={checkoutOpen} onOpenChange={handleCheckoutClose} />
    </Sheet>
  );
}
